import { ArrayUtils, engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { ShapeElement } from "../Components/Elements/Graphics/ShapeElement";
import { Tweens, TweensAttributes } from "../Components/Effects/Tweens";
import { Attributes } from "../Utils/Attributes";

/**
 * ShapeElement allows you to display vector art in the display list.
 * Example:
 *   <ShapeElement Class="ShapeElement" X="0" Y="0" Width="100" Height="100" Color="FF0000" Mask="ShapeElement" />
 *
 * @class ShapeElement
 * @extends PIXI.Graphics
 * @implements IElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class AdvancedShapeElement extends ShapeElement {
    public updateByNode(node: Element): void {
        super.updateByNode(node);

        this.runTween(node);
    }

    /**
     *
     * @param {Element} node
     * @returns {boolean}
     */
    public runTween(node: Element): boolean {
        if (node.hasAttribute(TweensAttributes.TWEEN)) {
            let tweens: Array<string> = ArrayUtils.stringArray(node.getAttribute(TweensAttributes.TWEEN));

            for (let i: number = 0; i < tweens.length; i++) {
                //return if there are no values to tween
                if (!node.hasAttribute(TweensAttributes.TWEEN_VALUES + i)) {
                    return false;
                }

                let tween: string = tweens[i];

                const tweenValues: Array<number> = this.formatTweenValues(
                    tween,
                    ArrayUtils.stringArray(node.getAttribute(TweensAttributes.TWEEN_VALUES + i)),
                );
                const tweenDuration: Array<number> = ArrayUtils.numberArray(
                    node.getAttribute(TweensAttributes.TWEEN_DURATION + i) || "120",
                );
                const tweenEase: Array<string> = ArrayUtils.stringArray(
                    node.getAttribute(TweensAttributes.TWEEN_EASE + i) || "linear",
                );
                const tweenDelay: Array<number> = ArrayUtils.numberArray(
                    node.getAttribute(TweensAttributes.TWEEN_DELAY + i) || "0",
                );
                const tweenLoop: boolean = Attributes.assignBooleanAttribute(
                    node,
                    TweensAttributes.TWEEN_LOOP + i,
                    null,
                );

                if (tween === "scale") {
                    tween = "scale.x";
                    tweens[i] = "scale.y";
                    i--;
                }

                Tweens.customTween(this, { tween, tweenValues, tweenEase, tweenDuration, tweenDelay, tweenLoop });
            }
        }

        return false;
    }

    private formatTweenValues(tween: string, values: Array<string>): Array<number> {
        let tweenValues: Array<number> = [];

        if (tween === "scale") {
            tween = "scale.x";
        }
        let property: Array<string> = tween.split(".");
        let value: number;

        for (let i: number = 0; i < values.length; i++) {
            let current = values[i];
            switch (current[current.length - 1]) {
                case "c": //if we want current value
                    if (property.length == 1) {
                        value = this[property[0]];
                        if (property[0] === "rotation") {
                            value = this.radians2degrees(value);
                        }
                        tweenValues.push(value);
                    } else if (property.length == 2) {
                        tweenValues.push(this[property[0]][property[1]]);
                    }

                    break;
                case "+":
                    value = parseFloat(current.slice(0, -1));
                    if (tweenValues.length > 0) {
                        value = tweenValues[tweenValues.length - 1] + value;
                    }
                    tweenValues.push(value);
                    break;
                case "-":
                    value = parseFloat(current.slice(0, -1));
                    if (tweenValues.length > 0) {
                        value = tweenValues[tweenValues.length - 1] - value;
                    }
                    tweenValues.push(value);
                    break;
                default:
                    tweenValues.push(parseFloat(current));
            }
        }

        return tweenValues;
    }

    clone(): AdvancedShapeElement {
        var element: AdvancedShapeElement = new AdvancedShapeElement();

        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.rotation = this.rotation;
        element.visible = this.visible;
        element.alpha = this.alpha;
        element.tint = this.tint;
        element.blendMode = this.blendMode;
        element.name = this.name;

        element.clear();
        element.beginFill(this._color, this._fillAlpha);
        element.lineStyle(this._lineWidth, this._lineColor);
        element.drawRect(0, 0, this._width, this._height);
        element.endFill();

        return element;
    }

    /**
     *
     * @param degrees
     * @returns {number}
     */
    public radians2degrees(radians: number): number {
        return radians * (180 / Math.PI);
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/AdvancedShapeElement.ts
 **/